/* global axios */
/* eslint-disable max-classes-per-file */
import ApiClient from './ApiClient';

class LegalActivitiesAPI extends ApiClient {
  constructor() {
    super('legal-activities', { accountScoped: true });
  }

  list(params = {}) {
    return axios.get(this.url, { params });
  }

  show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  create(data = {}) {
    return axios.post(this.url, data);
  }

  update(id, data = {}) {
    return axios.patch(`${this.url}/${id}`, data);
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  complete(id) {
    return axios.patch(`${this.url}/${id}/complete`);
  }

  updateSeries(id, data = {}) {
    return axios.patch(`${this.url}/${id}/update_series`, data);
  }

  reports(params = {}) {
    return axios.get(`${this.url}/reports`, { params });
  }

  listReminders(activityId) {
    return axios.get(`${this.url}/${activityId}/reminders`);
  }

  createReminder(activityId, data = {}) {
    return axios.post(`${this.url}/${activityId}/reminders`, {
      reminder: data,
    });
  }

  deleteReminder(activityId, reminderId) {
    return axios.delete(`${this.url}/${activityId}/reminders/${reminderId}`);
  }

  // ── Checklists (Kanban Trello-like) ──────────────────────────────────────
  get accountRoot() {
    return this.url.replace(/\/legal-activities$/, '');
  }

  listChecklists(activityId) {
    return axios.get(`${this.url}/${activityId}/checklists`);
  }

  createChecklist(activityId, title) {
    return axios.post(`${this.url}/${activityId}/checklists`, { title });
  }

  deleteChecklist(checklistId) {
    return axios.delete(
      `${this.accountRoot}/legal-activity-checklists/${checklistId}`
    );
  }

  addChecklistItem(checklistId, content) {
    return axios.post(
      `${this.accountRoot}/legal-activity-checklists/${checklistId}/items`,
      { content }
    );
  }

  updateChecklistItem(itemId, data = {}) {
    return axios.patch(
      `${this.accountRoot}/legal-activity-checklist-items/${itemId}`,
      data
    );
  }

  deleteChecklistItem(itemId) {
    return axios.delete(
      `${this.accountRoot}/legal-activity-checklist-items/${itemId}`
    );
  }

  // ── Comentários (Kanban Trello-like) ─────────────────────────────────────
  listComments(activityId) {
    return axios.get(`${this.url}/${activityId}/comments`);
  }

  createComment(activityId, body) {
    return axios.post(`${this.url}/${activityId}/comments`, { body });
  }

  deleteComment(activityId, commentId) {
    return axios.delete(`${this.url}/${activityId}/comments/${commentId}`);
  }

  // ── GED (Sub-fase AGE-1 do roadmap de paridade Legal One — Agenda) ──────
  getDocuments(activityId) {
    return axios.get(`${this.url}/${activityId}/documents`);
  }

  // Anexar pela tarefa (06/10/2026, relato do Dr. Henrique): o servidor grava
  // no GED, liga à tarefa e, se ela é de um processo, põe na pasta dele.
  anexarDocumento(activityId, arquivo) {
    const formData = new FormData();
    formData.append('file', arquivo);
    return axios.post(`${this.url}/${activityId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Tira da tarefa; o documento continua no GED e na pasta do processo.
  desanexarDocumento(activityId, linkId) {
    return axios.delete(`${this.url}/${activityId}/documents/${linkId}`);
  }

  // 🔴 06/10/2026: este método morava, por engano, na classe dos SUBTIPOS
  // (`activityTypeSubtypesAPI`). `legalActivitiesAPI.conferirChoque` não
  // existia, o aviso de choque engolia o erro e nunca aparecia — a pessoa só
  // descobria o choque ao salvar, pela recusa do servidor (relato do Dr.
  // Henrique: "o sistema não deixa lançar"). Spec: api/specs/legalActivities.
  /**
   * Já tem coisa nesse horário para essas pessoas? Só lê — a tela pergunta
   * enquanto se preenche, antes de gravar qualquer coisa.
   */
  conferirChoque({
    start_at: comeca,
    end_at: termina,
    user_ids: pessoas,
    ignorar_id: ignorar,
  }) {
    return axios.get(`${this.url}/conferir-choque`, {
      params: {
        start_at: comeca,
        end_at: termina,
        user_ids: pessoas,
        ignorar_id: ignorar,
      },
    });
  }
}

class ActivityTypeSubtypesAPI extends ApiClient {
  constructor() {
    super('activity-type-subtypes', { accountScoped: true });
  }

  list(params = {}) {
    return axios.get(this.url, { params });
  }

  create(data = {}) {
    return axios.post(this.url, data);
  }

  update(id, data = {}) {
    return axios.patch(`${this.url}/${id}`, data);
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }
}

export const legalActivitiesAPI = new LegalActivitiesAPI();

// Os documentos da tarefa no formato da `ListaDeAnexos` — a mesma peça dos
// anexos do financeiro, para o arquivo da tarefa se ver e se baixar igual.
// O id do anexo é o do VÍNCULO: remover tira da tarefa, não apaga do GED.
const comoAnexo = doc => ({
  id: doc.link_id,
  filename: doc.file_name || doc.title,
  byte_size: doc.file_size,
  url: doc.file_preview_url || doc.file_url,
});

export const anexosDaTarefa = {
  async getAll(activityId) {
    const { data } = await legalActivitiesAPI.getDocuments(activityId);
    return { data: { payload: (data.payload || []).map(comoAnexo) } };
  },
  async upload(activityId, arquivo) {
    const { data } = await legalActivitiesAPI.anexarDocumento(
      activityId,
      arquivo
    );
    return { data: { payload: comoAnexo(data.payload) } };
  },
  delete(activityId, linkId) {
    return legalActivitiesAPI.desanexarDocumento(activityId, linkId);
  },
};
export const activityTypeSubtypesAPI = new ActivityTypeSubtypesAPI();
